import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  Checkbox,
  Flex,
  HStack,
  Heading,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Avatar } from '../components/Avatar';
import {
  ArrowLeft,
  CalendarDays,
  ChevronDown,
  CopyPlus,
  Repeat,
  SlidersHorizontal,
  UserRound,
  ChevronRight,
  GripVertical,
  MoreHorizontal,
  MoreVertical,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  currentFamilyIdAtom,
  fullScreenPageAtom,
  listsByFamilyAtom,
  listItemsByListAtom,
} from '../store';
import {
  useAuth,
  useFamily,
  useOnKeysArrived,
  useLoadingState,
} from '../hooks';
import { LoadingPanel } from '../components/brand/TwindenLoader';
import { isRetryableLoadError } from '../lib/loadErrors';
import {
  api,
  ApiError,
  type FamilyListItem,
  type FamilyMember,
  type ListCounts,
} from '../lib/api';
import { NoFamilySelected } from '../components/family/NoFamilySelected';
import { ConfirmSheet } from '../components/ConfirmSheet';
import { ProgressBar } from '../components/ProgressBar';
import { isE2EEReady, isLockedApp, writeSealedApp } from '../lib/e2ee/session';
import { newRecordId, saveSealedTemplate } from '../lib/e2ee/apps';
import {
  ListItemDetails,
  type ListItemChanges,
} from '../components/lists/ListItemDetails';
import { REPEAT_LABELS, dueLabel } from '../lib/lists';
import { t as translateNow, useT } from '../i18n';
import { isUnbound, UnboundMark } from '../components/UnboundMark';

interface SortableItemProps {
  item: FamilyListItem;
  currentUserId: string | undefined;
  onToggle: (item: FamilyListItem, checked: boolean) => void;
  onDelete: (item: FamilyListItem) => void;
  onOpen: (item: FamilyListItem) => void;
}

// Due date, repeat and assignee under the item text (M26, D45–D47).
function ItemMeta({
  item,
  currentUserId,
}: {
  item: FamilyListItem;
  currentUserId: string | undefined;
}) {
  const t = useT();
  const due = item.dueDate ? dueLabel(item.dueDate) : null;
  const assignee = item.assignedToUser
    ? item.assignedTo === currentUserId
      ? t('common.you')
      : item.assignedToUser.displayName.split(' ')[0]
    : null;
  if (!due && !item.repeat && !assignee) return null;
  return (
    <HStack spacing={3} fontSize="13px" color="muted" pt="2px" flexWrap="wrap">
      {due && (
        <HStack
          spacing={1}
          color={due.overdue ? 'danger' : due.soon ? 'accent' : 'muted'}
          fontWeight={due.overdue || due.soon ? 700 : 400}
        >
          <CalendarDays size={13} aria-hidden="true" />
          <Text as="span">{due.text}</Text>
        </HStack>
      )}
      {item.repeat && (
        <HStack spacing={1}>
          <Repeat size={13} aria-hidden="true" />
          <Text as="span">{t(REPEAT_LABELS[item.repeat])}</Text>
        </HStack>
      )}
      {assignee && (
        <HStack spacing={1}>
          <UserRound size={13} aria-hidden="true" />
          <Text as="span">{assignee}</Text>
        </HStack>
      )}
    </HStack>
  );
}

// One checklist row (design: "Checklist · Groceries"): a big tick box, the
// item and its note, who added it, and a handle to drag it.
function SortableItem({
  item,
  currentUserId,
  onToggle,
  onDelete,
  onOpen,
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });
  const t = useT();
  // The face on the right: who it's for (D45), else who added it.
  const face = item.assignedToUser ?? item.addedByUser;
  const familyId = useAtomValue(currentFamilyIdAtom);
  const mine = (item.assignedTo ?? item.addedBy) === currentUserId;

  return (
    <HStack
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      opacity={isDragging ? 0.65 : 1}
      bg={isDragging ? 'surface' : undefined}
      spacing={3}
      align="flex-start"
      px={2}
      py={3}
      borderBottomWidth="1px"
      borderColor="line"
    >
      <Checkbox
        size="lg"
        mt="1px"
        isChecked={item.checked}
        onChange={(event) => onToggle(item, event.target.checked)}
        aria-label={item.text}
        sx={{
          '.chakra-checkbox__control': {
            w: '26px',
            h: '26px',
            borderRadius: '8px',
          },
        }}
      />
      <Box
        flex="1"
        minW={0}
        as="button"
        type="button"
        textAlign="left"
        onClick={() => onOpen(item)}
        aria-label={t('apps.listDetail.detailsFor', { text: item.text })}
      >
        <Text fontSize="17px" lineHeight="26px">
          {item.text}
        </Text>
        {isUnbound(item) && <UnboundMark record />}
        {item.note && (
          <Text fontSize="14px" color="muted">
            {item.note}
          </Text>
        )}
        <ItemMeta item={item} currentUserId={currentUserId} />
      </Box>
      {mine ? (
        <Box
          w="22px"
          h="22px"
          mt="2px"
          borderRadius="full"
          bg="accent.solid"
          color="accent.onSolid"
          fontSize="8px"
          fontWeight={700}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          role="img"
          aria-label={
            item.assignedTo
              ? t('apps.listDetail.forYou')
              : t('apps.listDetail.addedByYou')
          }
        >
          {t('common.you')}
        </Box>
      ) : (
        <Avatar
          name={face?.displayName}
          src={face?.avatarUrl ?? undefined}
          ownerId={face?.id}
          familyId={familyId}
          size="xs"
          mt="1px"
          role="img"
          aria-label={t(
            item.assignedTo
              ? 'apps.listDetail.forName'
              : 'apps.listDetail.addedByName',
            { name: face?.displayName ?? t('apps.listDetail.someone') }
          )}
        />
      )}
      <Menu placement="bottom-end" isLazy>
        <MenuButton
          as={IconButton}
          aria-label={t('apps.shared.moreFor', { name: item.text })}
          icon={<MoreHorizontal size={18} />}
          size="sm"
          variant="ghost"
          colorScheme="gray"
          color="muted"
        />
        <MenuList>
          {/* A locked item's text can't be resealed here (#82). */}
          {!isLockedApp(item) && (
            <MenuItem
              icon={<SlidersHorizontal size={16} />}
              onClick={() => onOpen(item)}
            >
              {t('apps.listDetail.detailsMenu')}
            </MenuItem>
          )}
          <MenuItem
            icon={<Trash2 size={16} />}
            color="danger"
            onClick={() => onDelete(item)}
          >
            {t('apps.listItem.delete')}
          </MenuItem>
        </MenuList>
      </Menu>
      <Box
        {...attributes}
        {...listeners}
        cursor="grab"
        color="muted"
        pt="3px"
        aria-label={t('apps.listDetail.reorder', { text: item.text })}
        sx={{ touchAction: 'none' }}
      >
        <GripVertical size={20} />
      </Box>
    </HStack>
  );
}

export function ListDetailPage() {
  const { listId } = useParams<{ listId: string }>();
  const { currentFamilyId, currentRole } = useFamily();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();

  const [listsMap, setListsMap] = useAtom(listsByFamilyAtom);
  const [itemsMap, setItemsMap] = useAtom(listItemsByListAtom);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Only a load the server couldn't answer gets Try again (D249); "List not
  // found" keeps its own message and the way back to the lists.
  const [loadFailure, setLoadFailure] = useState<unknown>(null);
  const [text, setText] = useState('');
  const [adding, setAdding] = useState(false);
  const [rename, setRename] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const renameDialog = useDisclosure();
  const deleteDialog = useDisclosure();
  const [doneOpen, setDoneOpen] = useState(true);
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [savingDetails, setSavingDetails] = useState(false);
  // Who items can be for (D45).
  const [members, setMembers] = useState<FamilyMember[]>([]);
  useEffect(() => {
    if (!currentFamilyId) return;
    let cancelled = false;
    api
      .getFamily(currentFamilyId)
      .then((response) => {
        if (!cancelled) setMembers(response.data.members);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [currentFamilyId]);

  // The checklist takes the whole screen on phones, with its own back button.
  const setFullScreen = useSetAtom(fullScreenPageAtom);
  useEffect(() => {
    setFullScreen(true);
    return () => setFullScreen(false);
  }, [setFullScreen]);

  const list = currentFamilyId
    ? (listsMap[currentFamilyId] ?? []).find((entry) => entry.id === listId)
    : undefined;
  const items = listId ? (itemsMap[listId] ?? []) : [];

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const canModerate = currentRole === 'root' || currentRole === 'admin';
  const canManageList =
    !!list && (list.createdBy === currentUser?.id || canModerate);
  // A list this device can't open (its key hasn't come, or a newer app
  // sealed it, #82) isn't renamed or kept as a template here: either would
  // write over, or copy, what it can't read.
  const listLocked = !!list && isLockedApp(list);

  const setItems = useCallback(
    (id: string, updater: (current: FamilyListItem[]) => FamilyListItem[]) => {
      setItemsMap((current) => ({
        ...current,
        [id]: updater(current[id] ?? []),
      }));
    },
    [setItemsMap]
  );

  const setCounts = useCallback(
    (familyId: string, id: string, counts: ListCounts) => {
      setListsMap((current) => ({
        ...current,
        [familyId]: (current[familyId] ?? []).map((entry) =>
          entry.id === id
            ? {
                ...entry,
                itemCount: counts.total,
                checkedCount: counts.checked,
              }
            : entry
        ),
      }));
    },
    [setListsMap]
  );

  const load = useCallback(async () => {
    if (!currentFamilyId || !listId) return;
    setLoading(true);
    setError(null);
    setLoadFailure(null);
    try {
      const response = await api.getFamilyList(currentFamilyId, listId);
      const { items: loadedItems, ...listData } = response.data;
      setListsMap((current) => ({
        ...current,
        [currentFamilyId]: [
          listData,
          ...(current[currentFamilyId] ?? []).filter(
            (entry) => entry.id !== listData.id
          ),
        ],
      }));
      setItemsMap((current) => ({ ...current, [listId]: loadedItems }));
    } catch (err) {
      setLoadFailure(isRetryableLoadError(err) ? err : null);
      setError(
        err instanceof ApiError
          ? err.message
          : translateNow('apps.listDetail.loadFailed')
      );
    } finally {
      setLoading(false);
    }
  }, [currentFamilyId, listId, setItemsMap, setListsMap]);

  // The mark while the list loads; a list already in the store shows at
  // once (M66, D248).
  const listState = useLoadingState({
    loading,
    hasData: Boolean(list),
    error: loadFailure,
    retry: () => void load(),
  });
  useEffect(() => {
    void load();
  }, [load]);
  // The family's key reached this device: open what was locked (M28).
  useOnKeysArrived('family', currentFamilyId, () => void load());

  const handleAdd = async () => {
    if (!currentFamilyId || !listId || !text.trim()) return;
    setAdding(true);
    try {
      // Item text is sealed for the family when this device can (M28, D65),
      // bound to an id chosen here (D329).
      const itemId = newRecordId();
      const response = isE2EEReady()
        ? await writeSealedApp(
            currentFamilyId,
            'item',
            { text: text.trim(), note: null },
            (sealed) =>
              api.addFamilyListItem(currentFamilyId, listId, {
                sealed,
                id: itemId,
              }),
            itemId,
            // An item is bound to its list (D329).
            listId
          )
        : await api.addFamilyListItem(currentFamilyId, listId, {
            text: text.trim(),
          });
      setItems(listId, (current) => [
        ...current.filter((item) => item.id !== response.data.item.id),
        response.data.item,
      ]);
      setCounts(currentFamilyId, listId, response.data.counts);
      setText('');
    } catch (err) {
      toast({
        title: t('apps.listDetail.addFailed'),
        description:
          err instanceof ApiError ? err.message : t('apps.shared.unknownError'),
        status: 'error',
      });
    } finally {
      setAdding(false);
    }
  };

  // One tick at a time per item: a double tap on a repeating item would
  // otherwise skip a whole period (D47).
  const togglingRef = useRef(new Set<string>());
  const handleToggle = async (item: FamilyListItem, checked: boolean) => {
    if (!currentFamilyId || !listId) return;
    if (togglingRef.current.has(item.id)) return;
    togglingRef.current.add(item.id);
    setItems(listId, (current) =>
      current.map((entry) =>
        entry.id === item.id ? { ...entry, checked } : entry
      )
    );
    try {
      const response = await api.updateFamilyListItem(
        currentFamilyId,
        listId,
        item.id,
        { checked }
      );
      setItems(listId, (current) =>
        current.map((entry) =>
          entry.id === item.id ? response.data.item : entry
        )
      );
      setCounts(currentFamilyId, listId, response.data.counts);
    } catch (err) {
      setItems(listId, (current) =>
        current.map((entry) => (entry.id === item.id ? item : entry))
      );
      toast({
        title: t('apps.listDetail.updateFailed'),
        description:
          err instanceof ApiError ? err.message : t('apps.shared.unknownError'),
        status: 'error',
      });
    } finally {
      togglingRef.current.delete(item.id);
    }
  };

  const handleDeleteItem = async (item: FamilyListItem) => {
    if (!currentFamilyId || !listId) return;
    try {
      const response = await api.deleteFamilyListItem(
        currentFamilyId,
        listId,
        item.id
      );
      setItems(listId, (current) =>
        current.filter((entry) => entry.id !== item.id)
      );
      setCounts(currentFamilyId, listId, response.data.counts);
    } catch (err) {
      toast({
        title: t('apps.listDetail.removeFailed'),
        description:
          err instanceof ApiError ? err.message : t('apps.shared.unknownError'),
        status: 'error',
      });
    }
  };

  const handleSaveDetails = async (changes: ListItemChanges) => {
    if (!currentFamilyId || !listId || !detailsId) return;
    const editing = items.find((item) => item.id === detailsId);
    if (editing && isLockedApp(editing)) return;
    setSavingDetails(true);
    try {
      const { text: newText, note: newNote, ...plainChanges } = changes;
      const current = items.find((item) => item.id === detailsId);
      const textChanged =
        !current ||
        current.text !== newText ||
        (current.note ?? null) !== newNote;
      const response =
        isE2EEReady() && textChanged
          ? await writeSealedApp(
              currentFamilyId,
              'item',
              { text: newText, note: newNote },
              (sealed) =>
                api.updateFamilyListItem(currentFamilyId, listId, detailsId, {
                  ...plainChanges,
                  sealed,
                }),
              detailsId,
              listId
            )
          : await api.updateFamilyListItem(
              currentFamilyId,
              listId,
              detailsId,
              isE2EEReady() ? plainChanges : changes
            );
      setItems(listId, (current) =>
        current.map((entry) =>
          entry.id === detailsId ? response.data.item : entry
        )
      );
      setCounts(currentFamilyId, listId, response.data.counts);
      setDetailsId(null);
    } catch (err) {
      toast({
        title: t('apps.listDetail.saveFailed'),
        description:
          err instanceof ApiError ? err.message : t('common.somethingWrong'),
        status: 'error',
      });
    } finally {
      setSavingDetails(false);
    }
  };

  // Keep this list to start new ones from (D48).
  const handleSaveTemplate = async () => {
    if (!currentFamilyId || !listId || listLocked) return;
    try {
      // Sealed again for the template's own ids (D329).
      await saveSealedTemplate(currentFamilyId, listId);
      toast({
        title: t('apps.listDetail.templateSaved'),
        description: t('apps.listDetail.templateSavedHint'),
        status: 'success',
      });
    } catch (err) {
      toast({
        title: t('apps.listDetail.templateFailed'),
        description:
          err instanceof ApiError ? err.message : t('common.somethingWrong'),
        status: 'error',
      });
    }
  };

  const handleClearChecked = async () => {
    if (!currentFamilyId || !listId) return;
    try {
      const response = await api.clearCheckedFamilyListItems(
        currentFamilyId,
        listId
      );
      setItems(listId, (current) => current.filter((entry) => !entry.checked));
      setCounts(currentFamilyId, listId, response.data.counts);
      toast({
        title: t(
          response.data.cleared === 1
            ? 'apps.listDetail.clearedOne'
            : 'apps.listDetail.clearedOther',
          { count: response.data.cleared }
        ),
        status: 'success',
      });
    } catch (err) {
      toast({
        title: t('apps.listDetail.clearFailed'),
        description:
          err instanceof ApiError ? err.message : t('apps.shared.unknownError'),
        status: 'error',
      });
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    if (!currentFamilyId || !listId) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(items, oldIndex, newIndex).map(
      (item, index) => ({ ...item, position: index })
    );
    setItems(listId, () => reordered);

    try {
      await api.updateFamilyListItem(
        currentFamilyId,
        listId,
        String(active.id),
        { position: newIndex }
      );
    } catch (err) {
      toast({
        title: t('apps.listDetail.reorderFailed'),
        description:
          err instanceof ApiError ? err.message : t('apps.shared.unknownError'),
        status: 'error',
      });
      void load();
    }
  };

  const handleRename = async () => {
    if (!currentFamilyId || !listId || !rename.trim() || listLocked) return;
    setRenaming(true);
    try {
      const response = isE2EEReady()
        ? await writeSealedApp(
            currentFamilyId,
            'name',
            { name: rename.trim() },
            (sealed) =>
              api.renameFamilyList(currentFamilyId, listId, { sealed }),
            listId
          )
        : await api.renameFamilyList(currentFamilyId, listId, rename.trim());
      setListsMap((current) => ({
        ...current,
        [currentFamilyId]: (current[currentFamilyId] ?? []).map((entry) =>
          entry.id === listId ? response.data : entry
        ),
      }));
      renameDialog.onClose();
    } catch (err) {
      toast({
        title: t('apps.listDetail.renameFailed'),
        description:
          err instanceof ApiError ? err.message : t('apps.shared.unknownError'),
        status: 'error',
      });
    } finally {
      setRenaming(false);
    }
  };

  const handleDeleteList = async () => {
    if (!currentFamilyId || !listId) return;
    setDeleting(true);
    try {
      await api.deleteFamilyList(currentFamilyId, listId);
      setListsMap((current) => ({
        ...current,
        [currentFamilyId]: (current[currentFamilyId] ?? []).filter(
          (entry) => entry.id !== listId
        ),
      }));
      navigate('/lists');
    } catch (err) {
      toast({
        title: t('apps.listDetail.deleteFailed'),
        description:
          err instanceof ApiError ? err.message : t('apps.shared.unknownError'),
        status: 'error',
      });
    } finally {
      setDeleting(false);
      deleteDialog.onClose();
    }
  };

  if (!currentFamilyId) return <NoFamilySelected />;

  if (listState.show) {
    return (
      <Center h="full">
        <LoadingPanel state={listState} label={t('apps.listDetail.loading')} />
      </Center>
    );
  }

  if (error || !list) {
    return (
      <VStack spacing={4} p={6} maxW="560px" mx="auto">
        <Alert status="error" borderRadius="xl">
          <AlertIcon />
          {error ?? t('apps.listDetail.notFound')}
        </Alert>
        <Button
          leftIcon={<ArrowLeft size={18} />}
          onClick={() => navigate('/lists')}
        >
          {t('apps.listDetail.backToLists')}
        </Button>
      </VStack>
    );
  }

  const open = items.filter((item) => !item.checked);
  const done = items.filter((item) => item.checked);

  return (
    <Flex direction="column" h="full" maxW="720px" mx="auto" w="full">
      {/* Back and options; the title sits below, large (design) */}
      <HStack
        spacing={1}
        px={2}
        pt={{ base: 'max(env(safe-area-inset-top), 0.5rem)', md: 2 }}
        h={{ base: 'auto', md: '60px' }}
        minH="60px"
        flexShrink={0}
      >
        <IconButton
          aria-label={t('apps.listDetail.backToLists')}
          icon={<ArrowLeft size={24} />}
          variant="ghost"
          colorScheme="gray"
          onClick={() => navigate('/lists')}
        />
        <Box flex="1" />
        <Menu placement="bottom-end" isLazy>
          <MenuButton
            as={IconButton}
            aria-label={t('apps.listDetail.options')}
            icon={<MoreVertical size={22} />}
            variant="ghost"
            colorScheme="gray"
          />
          <MenuList>
            {!listLocked && (
              <MenuItem
                icon={<CopyPlus size={16} />}
                onClick={() => void handleSaveTemplate()}
              >
                {t('apps.listDetail.saveTemplate')}
              </MenuItem>
            )}
            {canManageList && (
              <>
                {!listLocked && (
                  <MenuItem
                    icon={<Pencil size={16} />}
                    onClick={() => {
                      setRename(list.name);
                      renameDialog.onOpen();
                    }}
                  >
                    {t('apps.listDetail.rename')}
                  </MenuItem>
                )}
                <MenuItem
                  icon={<Trash2 size={16} />}
                  color="danger"
                  onClick={deleteDialog.onOpen}
                >
                  {t('apps.lists.deleteMenu')}
                </MenuItem>
              </>
            )}
          </MenuList>
        </Menu>
      </HStack>

      <VStack align="stretch" spacing={2} px={4} pb="14px" flexShrink={0}>
        <Heading as="h1" fontSize="32px" lineHeight={1}>
          {list.name}
        </Heading>
        {isUnbound(list) && <UnboundMark record />}
        <HStack spacing="10px">
          <ProgressBar
            done={list.checkedCount}
            total={list.itemCount}
            label={t('apps.listDetail.progress')}
            grow
          />
          <Text fontSize="14px" fontWeight={700} color="accent" flexShrink={0}>
            {t('apps.lists.progressCount', {
              checked: list.checkedCount,
              total: list.itemCount,
            })}
          </Text>
        </HStack>
      </VStack>

      <Box
        flex="1"
        minH={0}
        overflowY="auto"
        px={2}
        sx={{ overscrollBehaviorY: 'contain' }}
      >
        {items.length === 0 ? (
          <Text color="muted" textAlign="center" py={12} px={6}>
            {t('apps.listDetail.empty')}
          </Text>
        ) : (
          <>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={open.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                {open.map((item) => (
                  <SortableItem
                    key={item.id}
                    item={item}
                    currentUserId={currentUser?.id}
                    onToggle={handleToggle}
                    onDelete={handleDeleteItem}
                    onOpen={(target) => setDetailsId(target.id)}
                  />
                ))}
              </SortableContext>
            </DndContext>

            {done.length > 0 && (
              <>
                <HStack spacing={2} px={2} pt="18px" pb="6px">
                  <Button
                    flex="1"
                    justifyContent="flex-start"
                    variant="unstyled"
                    display="flex"
                    alignItems="center"
                    gap="6px"
                    minH="44px"
                    fontSize="15px"
                    fontWeight={700}
                    aria-expanded={doneOpen}
                    onClick={() => setDoneOpen((value) => !value)}
                  >
                    {doneOpen ? (
                      <ChevronDown size={18} />
                    ) : (
                      <ChevronRight size={18} />
                    )}
                    {t('apps.listDetail.doneCount', { count: done.length })}
                  </Button>
                  <Button
                    h="36px"
                    px="14px"
                    fontSize="14px"
                    variant="outline"
                    colorScheme="gray"
                    color="accent"
                    borderColor="line"
                    bg="surface"
                    onClick={handleClearChecked}
                  >
                    {t('apps.listDetail.clearDone')}
                  </Button>
                </HStack>
                {doneOpen &&
                  done.map((item) => (
                    <HStack key={item.id} spacing={3} px={2} py="10px">
                      <Checkbox
                        size="lg"
                        isChecked
                        onChange={(event) =>
                          handleToggle(item, event.target.checked)
                        }
                        aria-label={t('apps.listDetail.itemDone', {
                          text: item.text,
                        })}
                        sx={{
                          '.chakra-checkbox__control': {
                            w: '26px',
                            h: '26px',
                            borderRadius: '8px',
                          },
                        }}
                      />
                      <Text
                        flex="1"
                        fontSize="17px"
                        lineHeight="26px"
                        color="muted"
                        textDecoration="line-through"
                      >
                        {item.text}
                      </Text>
                    </HStack>
                  ))}
              </>
            )}
          </>
        )}
      </Box>

      {/* Add an item: a pill at the bottom (design) */}
      <Box
        as="form"
        onSubmit={(event: React.FormEvent) => {
          event.preventDefault();
          void handleAdd();
        }}
        flexShrink={0}
        px={3}
        pt={2}
        pb="max(env(safe-area-inset-bottom), 12px)"
        borderTopWidth="1px"
        borderColor="line"
      >
        <HStack
          spacing="6px"
          p="6px"
          bg="surface"
          borderRadius="30px"
          boxShadow="0 0 0 1px var(--chakra-colors-line)"
          _focusWithin={{ boxShadow: '0 0 0 2px var(--chakra-colors-accent)' }}
        >
          <Input
            placeholder={t('apps.listDetail.addPlaceholder')}
            aria-label={t('apps.listDetail.addPlaceholder')}
            value={text}
            onChange={(event) => setText(event.target.value)}
            maxLength={300}
            variant="unstyled"
            h="44px"
            px={3}
            fontSize="17px"
          />
          <IconButton
            type="submit"
            aria-label={t('apps.listDetail.addButton')}
            icon={<Plus size={22} />}
            isLoading={adding}
            isDisabled={!text.trim()}
            flexShrink={0}
          />
        </HStack>
      </Box>

      <Modal
        isOpen={renameDialog.isOpen}
        onClose={renameDialog.onClose}
        isCentered
      >
        <ModalOverlay bg="rgba(31, 29, 27, 0.5)" />
        <ModalContent mx={4} borderRadius="28px">
          <ModalHeader>
            <Heading as="h2" fontSize="24px">
              {t('apps.listDetail.rename')}
            </Heading>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input
              value={rename}
              onChange={(event) => setRename(event.target.value)}
              maxLength={100}
              autoFocus
              aria-label={t('apps.listDetail.listName')}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void handleRename();
              }}
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <Button
              variant="ghost"
              colorScheme="gray"
              onClick={renameDialog.onClose}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleRename}
              isLoading={renaming}
              isDisabled={!rename.trim()}
            >
              {t('common.save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmSheet
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.onClose}
        title={t('apps.lists.deleteTitle', { name: list.name })}
        body={t(
          list.itemCount === 1
            ? 'apps.listDetail.deleteBodyOne'
            : 'apps.listDetail.deleteBodyOther',
          { count: list.itemCount }
        )}
        confirmLabel={t('apps.lists.deleteConfirm')}
        confirmIcon={<Trash2 size={18} />}
        cancelLabel={t('apps.shared.keepIt')}
        onConfirm={() => void handleDeleteList()}
        isLoading={deleting}
      />

      <ListItemDetails
        item={
          items.find((item) => item.id === detailsId && !isLockedApp(item)) ??
          null
        }
        members={members}
        currentUserId={currentUser?.id}
        isSaving={savingDetails}
        onSave={(changes) => void handleSaveDetails(changes)}
        onDelete={() => {
          const target = items.find((item) => item.id === detailsId);
          setDetailsId(null);
          if (target) void handleDeleteItem(target);
        }}
        onClose={() => setDetailsId(null)}
      />
    </Flex>
  );
}
